/**
 * Finanzas → Cuadres (`/finanzas/cuadres`).
 *
 * Portado de `Cuadres` + `views/cuadres/{index,nuevo,ver,anular}.php` y de su
 * modelo `Cuadres_model`.
 *
 * La pantalla es el **archivo de cuadres mensuales**: de cada mes y año se sube el
 * Excel con el que se cuadra la contabilidad. De un período sólo puede haber **un
 * cuadre activo** —si ya existe, hay que anularlo antes de subir otro— y arriba se
 * ve cuántos hay, cuántos siguen activos y **qué meses del año están sin cuadrar**.
 *
 * Diferencias anotadas:
 * - El listado se **pagina y filtra** en el servidor (año, mes, estado y búsqueda
 *   libre), con los contadores del modelo del original y los meses pendientes que
 *   el original calculaba pero no enseñaba.
 * - El formulario **avisa mientras se escribe** si el período ya tiene cuadre
 *   activo (el original lo comprobaba al enviar) y la ficha comprueba la
 *   **integridad** del archivo (que siga en el servidor y con su tamaño).
 * - El archivo se guarda en el almacén de la API con una ruta **relativa**
 *   (`cuadres/2026/09/…`), no con el camino absoluto del disco.
 */

import {
  Ban,
  BadgeCheck,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileSpreadsheet,
  Info,
  Plus,
  RefreshCw,
  Search,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatDateValue } from '@/lib/utils';

import {
  DialogoAnularCuadre,
  DialogoDetalleCuadre,
  DialogoSubirCuadre,
} from '../components/cuadre-dialogos';
import { cuadresApi, useAnularCuadre, useCuadres, useDetalleCuadre, useSubirCuadre } from '../cuadres.api';
import type { Cuadre, FiltrosCuadres } from '../cuadres.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

export function CuadresPage() {
  useDocumentTitle('Finanzas · Cuadres');

  const [anio, setAnio] = useState(String(new Date().getFullYear()));
  const [mes, setMes] = useState('');
  const [estado, setEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(25);
  const [abierto, setAbierto] = useState<number | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [porAnular, setPorAnular] = useState<Cuadre | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorFormulario, setErrorFormulario] = useState<string | null>(null);
  const [errorAnulacion, setErrorAnulacion] = useState<string | null>(null);
  const [errorDescarga, setErrorDescarga] = useState<string | null>(null);

  const filtros = useMemo<FiltrosCuadres>(
    () => ({
      anio: anio ? Number(anio) : 0,
      mes: mes ? Number(mes) : 0,
      estado,
      buscar: busqueda.trim(),
      page: pagina,
      perPage: limite,
    }),
    [anio, mes, estado, busqueda, pagina, limite],
  );

  const consulta = useCuadres(filtros);
  const detalle = useDetalleCuadre(abierto);
  const subir = useSubirCuadre();
  const anular = useAnularCuadre();

  const cuadres = consulta.data?.cuadres ?? [];
  const contadores = consulta.data?.estadisticas;
  const pendientes = consulta.data?.meses_pendientes ?? [];
  const total = consulta.data?.total ?? 0;
  const paginas = consulta.data?.paginas ?? 1;

  /** Cambiar cualquier filtro vuelve a la primera página. */
  const filtrar = (cambio: () => void) => {
    setPagina(1);
    cambio();
  };

  const descargar = async (cuadre: Cuadre) => {
    setErrorDescarga(null);

    try {
      await abrirPdfAutenticado(cuadresApi.urlArchivo(cuadre.id), { nombre: cuadre.archivo });
    } catch (fallo) {
      setErrorDescarga(
        fallo instanceof Error ? fallo.message : 'No se pudo descargar el archivo del cuadre.',
      );
    }
  };

  const confirmarSubida = (datos: {
    mes: string;
    anio: string;
    observaciones: string;
    archivo: File | null;
  }) => {
    setErrorFormulario(null);

    subir.mutate(datos, {
      onSuccess: (resultado) => {
        setSubiendo(false);
        setAviso(resultado.message);
        setTimeout(() => setAviso(null), 8000);
      },
      onError: (fallo) => {
        setErrorFormulario(
          fallo instanceof Error ? fallo.message : 'No se pudo subir el cuadre.',
        );
      },
    });
  };

  const confirmarAnulacion = (motivo: string) => {
    if (!porAnular) return;

    setErrorAnulacion(null);

    anular.mutate(
      { id: porAnular.id, motivo },
      {
        onSuccess: (resultado) => {
          setAviso(resultado.message);
          setPorAnular(null);
          setAbierto(null);
          setTimeout(() => setAviso(null), 8000);
        },
        onError: (fallo) => {
          setErrorAnulacion(fallo instanceof Error ? fallo.message : 'No se pudo anular el cuadre.');
        },
      },
    );
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Cuadres</h1>
          <p className="mt-1 text-sm text-ink-500">
            Los cuadres mensuales en Excel: un archivo por período, con su subida, su descarga, su
            comprobación y su anulación.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            leftIcon={<Plus className="size-4" aria-hidden="true" />}
            onClick={() => {
              setErrorFormulario(null);
              setSubiendo(true);
            }}
          >
            Nuevo cuadre
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="No se pudo completar la acción">
          {error}
        </Alert>
      ) : null}

      {errorDescarga ? (
        <Alert variant="error" title="No se pudo descargar el archivo">
          {errorDescarga}
        </Alert>
      ) : null}

      {/* Contadores */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600"
            >
              <FileSpreadsheet className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Cuadres subidos
              </p>
              <p className="text-base font-bold text-ink-900 tabular-nums sm:text-lg">
                {(contadores?.total ?? 0).toLocaleString('es-PE')}
              </p>
              <p className="text-xs text-ink-500">
                {contadores?.del_anio ?? 0} en {contadores?.anio ?? ''}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700"
            >
              <BadgeCheck className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Activos
              </p>
              <p className="text-base font-bold text-emerald-800 tabular-nums sm:text-lg">
                {(contadores?.activos ?? 0).toLocaleString('es-PE')}
              </p>
              <p className="text-xs text-ink-500">períodos con cuadre vigente</p>
            </div>
          </CardContent>
        </Card>

        <Card className={cn((contadores?.anulados ?? 0) > 0 && 'border-rose-200 bg-rose-50/50')}>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-rose-100 text-rose-600"
            >
              <Ban className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Anulados
              </p>
              <p className="text-base font-bold text-rose-700 tabular-nums sm:text-lg">
                {(contadores?.anulados ?? 0).toLocaleString('es-PE')}
              </p>
              <p className="text-xs text-ink-500">con su motivo guardado</p>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(pendientes.length > 0 && 'border-amber-200 bg-amber-50/50')}>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700"
            >
              <CalendarClock className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Meses sin cuadrar
              </p>
              <p className="text-base font-bold text-amber-800 tabular-nums sm:text-lg">
                {pendientes.length}
              </p>
              <p className="text-xs text-ink-500">
                {pendientes.length > 0
                  ? pendientes
                      .slice(0, 3)
                      .map((item) => item.periodo.split(' ')[0])
                      .join(', ') + (pendientes.length > 3 ? '…' : '')
                  : 'todo el año al día'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Año
              <Select
                value={anio}
                onChange={(evento) => filtrar(() => setAnio(evento.target.value))}
                className={CONTROL}
                aria-label="Año"
              >
                <option value="">Todos los años</option>
                {(consulta.data?.anios ?? []).map((item) => (
                  <option key={item} value={String(item)}>
                    {item}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Mes
              <Select
                value={mes}
                onChange={(evento) => filtrar(() => setMes(evento.target.value))}
                className={CONTROL}
                aria-label="Mes"
              >
                <option value="">Todos los meses</option>
                {(consulta.data?.meses ?? []).map((item) => (
                  <option key={item.valor} value={String(item.valor)}>
                    {item.nombre}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Estado
              <Select
                value={estado}
                onChange={(evento) => filtrar(() => setEstado(evento.target.value))}
                className={CONTROL}
                aria-label="Estado"
              >
                <option value="">Todos</option>
                <option value="activo">Activos</option>
                <option value="anulado">Anulados</option>
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Filas
              <Select
                value={String(limite)}
                onChange={(evento) => filtrar(() => setLimite(Number(evento.target.value)))}
                className={CONTROL}
                aria-label="Filas por página"
              >
                {(consulta.data?.tamanos ?? [10, 25, 50, 100]).map((tamano) => (
                  <option key={tamano} value={String(tamano)}>
                    {tamano}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-xs font-medium text-ink-500">
              Buscar
              <span className="relative block">
                <Search
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <Input
                  value={busqueda}
                  onChange={(evento) => filtrar(() => setBusqueda(evento.target.value))}
                  placeholder="Archivo, observaciones, motivo, usuario…"
                  className="pl-9"
                  aria-label="Buscar cuadre"
                />
              </span>
            </label>

            <span className="ml-auto text-xs text-ink-500">
              {total.toLocaleString('es-PE')} cuadre{total === 1 ? '' : 's'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Listado */}
      <Card>
        <CardContent className="space-y-3 p-4">
          {consulta.isError ? (
            <Alert variant="error" title="No se pudieron leer los cuadres">
              {consulta.error instanceof ApiError
                ? consulta.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
            </Alert>
          ) : null}

          <div className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2 whitespace-nowrap">Período</th>
                  <th className="px-2 py-2">Archivo</th>
                  <th className="px-2 py-2 whitespace-nowrap">Tamaño</th>
                  <th className="px-2 py-2 whitespace-nowrap">Subido</th>
                  <th className="px-2 py-2">Estado</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {consulta.isLoading
                  ? Array.from({ length: 5 }).map((_, fila) => (
                      <tr key={fila} className="border-t border-ink-100">
                        {Array.from({ length: 6 }).map((__, columna) => (
                          <td key={columna} className="px-2 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : cuadres.map((fila) => (
                      <tr
                        key={fila.id}
                        className={cn(
                          'border-t border-ink-100',
                          !fila.activo && 'bg-rose-50/40 text-ink-500',
                        )}
                      >
                        <td className="px-2 py-2 font-semibold whitespace-nowrap text-ink-800">
                          {fila.periodo}
                        </td>

                        <td className="max-w-[20rem] px-2 py-2">
                          <p className="truncate" title={fila.archivo}>
                            {fila.archivo}
                          </p>
                          {fila.observaciones ? (
                            <p className="truncate text-[11px] text-ink-500">
                              {fila.observaciones}
                            </p>
                          ) : null}
                        </td>

                        <td className="px-2 py-2 whitespace-nowrap tabular-nums">
                          {fila.tamanio_texto}
                        </td>

                        <td className="px-2 py-2 whitespace-nowrap">
                          <p>{formatDateValue(fila.creado?.slice(0, 10))}</p>
                          <p className="text-[11px] text-ink-500">{fila.creado_por}</p>
                        </td>

                        <td className="px-2 py-2">
                          {fila.activo ? (
                            <Badge variant="success">Activo</Badge>
                          ) : (
                            <Badge variant="danger">Anulado</Badge>
                          )}
                        </td>

                        <td className="px-2 py-2">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Ver el cuadre"
                              aria-label={`Ver el cuadre ${fila.periodo}`}
                              onClick={() => {
                                setError(null);
                                setAbierto(fila.id);
                              }}
                            >
                              <Eye className="size-4" aria-hidden="true" />
                            </Button>

                            {fila.disponible ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Descargar el archivo"
                                aria-label={`Descargar ${fila.archivo}`}
                                onClick={() => void descargar(fila)}
                              >
                                <Download className="size-4 text-emerald-600" aria-hidden="true" />
                              </Button>
                            ) : null}

                            {fila.activo ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Anular el cuadre"
                                aria-label={`Anular el cuadre ${fila.periodo}`}
                                onClick={() => {
                                  setErrorAnulacion(null);
                                  setPorAnular(fila);
                                }}
                              >
                                <Ban className="size-4 text-rose-600" aria-hidden="true" />
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-ink-500">
              Página {pagina} de {paginas.toLocaleString('es-PE')}
              {total > 0
                ? ` · mostrando ${((pagina - 1) * limite + 1).toLocaleString('es-PE')}–${Math.min(
                    pagina * limite,
                    total,
                  ).toLocaleString('es-PE')}`
                : ''}
            </span>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagina <= 1}
                leftIcon={<ChevronLeft className="size-4" aria-hidden="true" />}
                onClick={() => setPagina((actual) => Math.max(1, actual - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagina >= paginas}
                rightIcon={<ChevronRight className="size-4" aria-hidden="true" />}
                onClick={() => setPagina((actual) => actual + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        De cada mes y año puede haber un solo cuadre activo: para subir otro hay que anular el
        anterior, que conserva su archivo, su motivo y quién lo anuló.
      </p>

      {subiendo ? (
        <DialogoSubirCuadre
          abierto={subiendo}
          anios={consulta.data?.anios ?? []}
          meses={consulta.data?.meses ?? []}
          hoy={consulta.data?.hoy ?? new Date().toISOString().slice(0, 10)}
          guardando={subir.isPending}
          error={errorFormulario}
          onCerrar={() => {
            setSubiendo(false);
            setErrorFormulario(null);
          }}
          onSubir={confirmarSubida}
        />
      ) : null}

      <DialogoDetalleCuadre
        key={`cuadre-${abierto ?? 'cerrado'}`}
        cuadreId={abierto}
        consulta={detalle.data}
        cargando={detalle.isLoading}
        guardando={anular.isPending}
        error={abierto ? error : null}
        onCerrar={() => {
          setAbierto(null);
          setError(null);
        }}
        onAnular={(fila) => {
          setErrorAnulacion(null);
          setPorAnular(fila);
        }}
      />

      {porAnular ? (
        <DialogoAnularCuadre
          key={`anular-${porAnular.id}`}
          cuadre={porAnular}
          guardando={anular.isPending}
          error={errorAnulacion}
          onCerrar={() => {
            setPorAnular(null);
            setErrorAnulacion(null);
          }}
          onConfirmar={confirmarAnulacion}
        />
      ) : null}
    </div>
  );
}
